import "@babel/polyfill";
// import { User } from "../types";

/**
 * Mock authentication service simulating API calls to a database.
 * Enhanced with real-world email domain validation via DNS-over-HTTPS.
 */

const STORAGE_KEY = "headshot_studio_user";

// Standard email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// List of common disposable email providers to block
const DISPOSABLE_DOMAINS = [
  "mailinator.com",
  "10minutemail.com",
  "guerrillamail.com",
  "temp-mail.org",
  "dispostable.com",
  "getnada.com",
  "throwawaymail.com",
  "yopmail.com",
];

/**
 * Verifies if a domain has valid MX (Mail Exchange) records using Google DNS API.
 * This ensures the email is from a "real world" domain configured for mail.
 */
const verifyMxRecords = async (domain) => {
  try {
    // Using Google DNS-over-HTTPS to check MX records
    const response = await fetch(`https://dns.google/resolve?name=${domain}&type=MX`);
    const data = await response.json();

    // Status 0 is NOERROR in DNS parlance
    // We check if there are any Answer records returned for the MX type
    return data.Status === 0 && Array.isArray(data.Answer) && data.Answer.length > 0;
  } catch (e) {
    console.error("DNS Verification failed, falling back to basic check", e);
    return true; // Fallback to not block users if DNS service is down
  }
};

export const authService = {
  /**
   * Simulates a login API call
   */
  login: async (email, password) => {
    // Artificial delay to simulate network latency
    await new Promise((resolve) => setTimeout(resolve, 800));

    if (!EMAIL_REGEX.test(email)) {
      throw new Error("Please enter a valid email address.");
    }

    // Simple mock validation
    if (password.length < 6) {
      throw new Error("Password must be at least 6 characters.");
    }

    const mockUser = {
      id: Math.random().toString(36).substring(2, 11),
      email: email,
      name: email.split("@")[0],
      isGuest: false,
      createdAt: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(mockUser));
    return mockUser;
  },

  /**
   * Simulates a registration API call with Real-World Validation
   */
  register: async (email, password, name) => {
    // 1. Basic Format Validation
    if (!EMAIL_REGEX.test(email)) {
      throw new Error("Invalid email format. Expected example@domain.com");
    }

    const domain = email.split("@")[1].toLowerCase();

    // 2. Disposable Email Check
    if (DISPOSABLE_DOMAINS.includes(domain)) {
      throw new Error(
        "Disposable email addresses are not permitted. Please use a permanent email."
      );
    }

    // 3. DNS-over-HTTPS Real World Domain Validation
    // This checks if the domain actually has mail servers (MX records)
    const hasMailServers = await verifyMxRecords(domain);
    if (!hasMailServers) {
      throw new Error(
        `The domain '${domain}' does not appear to have valid mail servers. Please check for typos.`
      );
    }

    await new Promise((resolve) => setTimeout(resolve, 500));

    if (password.length < 6) {
      throw new Error("Password too short. Must be at least 6 characters.");
    }

    if (!name || name.trim().length < 2) {
      throw new Error("Please enter your full name.");
    }

    const mockUser = {
      id: Math.random().toString(36).substring(2, 11),
      email,
      name,
      isGuest: false,
      createdAt: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(mockUser));
    return mockUser;
  },

  /**
   * Continues as a guest session
   */
  loginAsGuest: async () => {
    await new Promise((resolve) => setTimeout(resolve, 300));

    const guestUser = {
      id: `guest_${Math.random().toString(36).substring(2, 9)}`,
      email: "guest@proheadshot.ai",
      name: "Guest User",
      isGuest: true,
      createdAt: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(guestUser));
    return guestUser;
  },

  /**
   * Gets the current user from local storage (persistence)
   */
  getCurrentUser: () => {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch (e) {
      return null;
    }
  },

  /**
   * Logs out and clears the session
   */
  logout: () => {
    localStorage.removeItem(STORAGE_KEY);
  },
};
